import { useTranslation } from "react-i18next"
import "./footer.css"

/* Footer de tutellus.com (Tutellus Footer Component 2.0), portado del handoff de
   Claude Design en landing-factory (apps/tutellus.com/src/components/Footer.astro).
   Aquí va como JSX real (i18n + tema): los textos pasan por el namespace `footer`
   y el wordmark del logo usa `currentColor` para seguir light/dark. Adaptaciones
   para la dapp (otro dominio): los enlaces de tutellus.com se sirven absolutos y
   abren en pestaña nueva para no sacar al usuario de la dapp. */

const SITE = "https://tutellus.com"
const UTM = "utm_source=footer&utm_medium=organic&utm_campaign=navegacion_web&utm_term=footer"

const COLUMNS = [
  {
    id: "emprende",
    titleKey: "sections.emprende",
    links: [
      { key: "links.tok120", href: `${SITE}/emprende/tok120/?${UTM}` },
      { key: "links.taller", href: `https://tallertokenizacion.com/?${UTM}` },
      { key: "links.packRwa", href: `${SITE}/emprende/pack-rwa` },
      { key: "links.builders", href: `${SITE}/builders` },
      { key: "links.certificaciones", href: `${SITE}/certificaciones` },
      {
        key: "links.consultoria",
        href: "https://calendly.com/d/cwzr-ykv-y9r/sesion-sobre-tokenizacion-agenda-tu-llamada-rrss",
      },
    ],
  },
  {
    id: "invierte",
    titleKey: "sections.invierte",
    links: [
      { key: "links.inversionSegura", href: `${SITE}/invierte/inversion-segura/?${UTM}` },
      { key: "links.defiAvanzado", href: `${SITE}/invierte/defi-avanzado/?${UTM}` },
      { key: "links.club", href: `${SITE}/invierte/club` },
    ],
  },
  {
    id: "ecosistema",
    titleKey: "sections.ecosistema",
    links: [
      { key: "links.stakiny", href: `https://stakiny.com/?${UTM}` },
      { key: "links.gt3", href: `https://dapp.gt3.finance/?${UTM}` },
      { key: "links.dappComposer", href: `${SITE}/dappcomposer` },
      { key: "links.humanWallet", href: `${SITE}/humanwallet` },
      { key: "links.comunidad", href: `https://comunidad.tutellus.com/?${UTM}` },
      { key: "links.criptoblog", href: `https://criptoblog.tutellus.com/?${UTM}` },
      {
        key: "links.mafia",
        href: `https://criptoblog.tutellus.com/post/tutellus-mafia-promoviendo-el-ecosistema-cripto-desde-el-principio/?${UTM}`,
      },
    ],
  },
] as const

const LEGAL = [
  { key: "legal.privacy", href: `${SITE}/politica-de-privacidad` },
  { key: "legal.cookies", href: `${SITE}/politica-de-cookies` },
  { key: "legal.legalNotice", href: `${SITE}/aviso-legal` },
] as const

const SOCIAL = [
  {
    label: "X",
    href: "https://x.com/tutellus",
    brand: true,
    icon: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/tutellus/",
    brand: false,
    icon: (
      <>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/tutellus/",
    brand: false,
    icon: (
      <>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </>
    ),
  },
  {
    label: "Telegram",
    href: "https://t.me/TutellusOficial",
    brand: false,
    icon: (
      <>
        <path d="m22 2-7 20-4-9-9-4Z" />
        <path d="M22 2 11 13" />
      </>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@Tutellus",
    brand: false,
    icon: (
      <>
        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
        <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
      </>
    ),
  },
] as const

const TutellusLogo = () => (
  <svg viewBox="0 0 450 100" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Tutellus">
    <g clipPath="url(#tt-foot-logo-clip0)">
      <g clipPath="url(#tt-foot-logo-clip1)">
        <path
          d="M69.0749 96.2028C73.1308 94.5302 76.949 92.3313 80.4313 89.6627C83.0527 87.647 85.4667 85.3752 87.6377 82.8809C85.6322 85.0962 83.2443 86.9326 80.5882 88.3021C76.8943 90.2378 72.7696 91.2049 68.6001 91.113C64.4307 91.0211 60.3526 89.8731 56.7476 87.7765C54.5494 86.5128 52.5496 84.9317 50.8129 83.0843C50.2135 82.462 49.6475 81.8084 49.1173 81.1261C47.6229 79.2675 46.2656 77.3026 45.0563 75.2471C43.3251 72.2757 41.9045 69.1339 40.8172 65.8713C37.7336 56.673 37.3934 46.7762 39.838 37.3879C40.1263 36.2901 40.4484 35.2008 40.813 34.1157C34.7502 32.8867 28.5118 32.7947 22.4154 33.8444C18.7642 34.4418 15.2916 35.8435 12.2489 37.9482C9.2063 40.0529 6.66984 42.8078 4.82326 46.0134C2.81956 49.478 1.681 53.3747 1.50409 57.3729C1.35552 60.6554 1.85535 63.935 2.97503 67.0242L3.0895 67.3464C7.12369 78.2927 14.8723 87.476 24.9843 93.2952C31.6175 97.1305 39.0446 99.3877 46.6903 99.8919C54.3361 100.396 61.9954 99.1339 69.0749 96.2028V96.2028Z"
          fill="url(#tt-foot-logo-p0)"
        />
        <path
          d="M80.4578 10.3534C76.9818 7.67554 73.1676 5.46793 69.114 3.78778C66.061 2.53009 62.89 1.58018 59.6482 0.952148C62.5697 1.57922 65.3541 2.72789 67.8678 4.34304C71.3871 6.57628 74.2828 9.66511 76.2843 13.3209C78.2859 16.9767 79.328 21.0801 79.3133 25.2479C79.3181 27.7845 78.9496 30.3081 78.2196 32.7375C77.9789 33.5653 77.6959 34.3802 77.3717 35.1789C76.5089 37.4012 75.4845 39.5574 74.3069 41.6301C72.5989 44.6156 70.5889 47.4178 68.3086 49.9929C61.8878 57.2577 53.4927 62.4989 44.1459 65.0781C43.0522 65.3791 41.9459 65.6461 40.8225 65.8707C42.7891 71.7363 45.8293 77.1851 49.7881 81.9393C52.1316 84.8013 55.0819 87.1066 58.4258 88.6886C61.7696 90.2705 65.4233 91.0895 69.1225 91.0862C73.1255 91.0897 77.0704 90.1287 80.6231 88.2845C83.5407 86.7694 86.133 84.6972 88.2534 82.1851L88.4739 81.9224C95.9384 72.9564 100.018 61.6546 99.9999 49.9886C100.004 42.3254 98.2439 34.764 94.8549 27.8906C91.466 21.0172 86.5397 15.0165 80.4578 10.3534Z"
          fill="url(#tt-foot-logo-p1)"
        />
        <path
          d="M0.432431 43.4324C-0.144144 47.782 -0.144144 52.1886 0.432431 56.5382C0.861818 59.8129 1.61878 63.0362 2.69189 66.1598C1.77209 63.3158 1.37287 60.3292 1.51341 57.3435C1.70156 53.1524 2.94277 49.0762 5.12247 45.4914C7.30217 41.9067 10.3501 38.9288 13.9847 36.8329C16.1461 35.5828 18.4822 34.6627 20.9156 34.1032C21.7555 33.9178 22.6045 33.7763 23.4591 33.6793C25.8334 33.3218 28.2305 33.1362 30.6316 33.1241C34.0712 33.1081 37.5033 33.4476 40.8732 34.1371C50.3811 36.0677 59.1223 40.7213 66.032 47.5311C66.8374 48.328 67.6217 49.1545 68.3762 50.0149C72.4858 45.3868 75.687 40.0262 77.8124 34.2134C79.0993 30.7499 79.6099 27.046 79.3081 23.3635C79.0063 19.6809 77.8996 16.1096 76.0659 12.9017C74.0683 9.43292 71.2632 6.49728 67.8887 4.34391C65.12 2.57522 62.0305 1.36835 58.7959 0.791978L58.4568 0.728389C46.947 -1.26183 35.1038 0.856614 24.9979 6.71331C18.3595 10.5382 12.6907 15.8397 8.43077 22.2072C4.17085 28.5747 1.43407 35.8373 0.432431 43.4324Z"
          fill="url(#tt-foot-logo-p2)"
        />
      </g>
    </g>
    <path
      d="M428.936 81.0113C422.084 81.0113 416.808 79.5828 413.11 76.726C409.412 73.8083 407.563 69.7054 407.563 64.4171V64.1436H419.799V64.9642C419.799 67.0309 420.452 68.4289 421.757 69.1583C423.117 69.8269 425.537 70.1613 429.018 70.1613C432.281 70.1613 434.429 69.8573 435.463 69.2495C436.55 68.5809 437.094 67.5779 437.094 66.2407C437.094 64.9642 436.686 64.0828 435.87 63.5965C435.055 63.0495 433.45 62.5936 431.057 62.2289L419.555 60.6789C415.476 60.1926 412.321 58.9162 410.091 56.8495C407.916 54.7828 406.828 51.8044 406.828 47.9142C406.828 45.2397 407.535 42.8387 408.949 40.7112C410.363 38.523 412.539 36.7907 415.476 35.5142C418.467 34.1769 422.301 33.5083 426.978 33.5083C431.438 33.5083 435.218 34.1465 438.318 35.423C441.472 36.6387 443.865 38.4926 445.497 40.9848C447.128 43.4769 447.944 46.5769 447.944 50.2848V50.5583H435.707V50.0112C435.707 48.7348 435.463 47.7014 434.973 46.9113C434.538 46.0603 433.641 45.422 432.281 44.9965C430.976 44.5711 429.072 44.3583 426.57 44.3583C423.525 44.3583 421.485 44.6926 420.452 45.3613C419.419 45.9691 418.902 47.0024 418.902 48.4612C418.902 49.6162 419.31 50.4975 420.126 51.1054C420.996 51.6524 422.763 52.1387 425.428 52.5642L434.157 53.7495C439.596 54.4181 443.457 55.8162 445.742 57.9436C448.026 60.0711 449.168 63.0191 449.168 66.7877C449.168 69.6446 448.352 72.1367 446.72 74.2642C445.089 76.3916 442.777 78.0632 439.786 79.2789C436.795 80.4338 433.178 81.0113 428.936 81.0113Z"
      fill="currentColor"
    />
    <path
      d="M371.529 81.0111C367.722 81.0111 364.541 80.2209 361.985 78.6405C359.483 76.9994 357.607 74.8111 356.356 72.0758C355.159 69.3405 354.561 66.2709 354.561 62.867V34.4199H366.798V58.3082C366.798 61.9552 367.586 64.6297 369.164 66.3317C370.741 67.9729 373.46 68.7935 377.322 68.7935C381.346 68.7935 384.147 67.9121 385.724 66.1493C387.356 64.3258 388.172 61.469 388.172 57.5788L389.722 57.4876L390.619 66.1493H388.335C388.008 68.7023 387.193 71.1033 385.887 73.3523C384.636 75.6013 382.815 77.4552 380.422 78.9141C378.083 80.3121 375.119 81.0111 371.529 81.0111ZM400.408 80.0994H388.987V65.967L388.172 65.5111V34.4199H400.408V80.0994Z"
      fill="currentColor"
    />
    <path d="M346.606 80.0995H334.369V19.0112H346.606V80.0995Z" fill="currentColor" />
    <path d="M326.332 80.0995H314.095V19.0112H326.332V80.0995Z" fill="currentColor" />
    <path
      d="M285.248 81.0113C280.734 81.0113 276.737 80.1603 273.256 78.4583C269.776 76.7564 267.083 74.1426 265.18 70.6171C263.276 67.0917 262.325 62.6544 262.325 57.3054C262.325 52.4426 263.276 48.2485 265.18 44.723C267.083 41.1367 269.721 38.371 273.093 36.4259C276.519 34.4809 280.49 33.5083 285.004 33.5083C289.463 33.5083 293.352 34.3593 296.669 36.0612C300.041 37.7632 302.652 40.2858 304.501 43.6289C306.35 46.9113 307.275 50.923 307.275 55.6642C307.275 56.4544 307.248 57.1838 307.193 57.8524C307.139 58.5211 307.057 59.2505 306.948 60.0407H270.238V52.8377H298.301L295.527 57.3054C295.527 56.6975 295.527 56.1201 295.527 55.573C295.527 55.026 295.527 54.4789 295.527 53.9318C295.527 50.4671 294.684 47.975 292.998 46.4554C291.312 44.9358 288.566 44.176 284.759 44.176C280.626 44.176 277.743 45.0877 276.112 46.9113C274.48 48.674 273.664 51.5005 273.664 55.3907V58.8554C273.664 62.7456 274.48 65.6024 276.112 67.426C277.743 69.1887 280.653 70.0701 284.84 70.0701C288.43 70.0701 290.959 69.5534 292.427 68.5201C293.896 67.4867 294.63 65.9975 294.63 64.0524V63.1407H306.867V64.1436C306.867 67.426 305.969 70.3436 304.175 72.8966C302.38 75.4495 299.851 77.4554 296.588 78.9142C293.379 80.3122 289.599 81.0113 285.248 81.0113Z"
      fill="currentColor"
    />
    <path
      d="M257.334 80.0993H247.218C242.432 80.0993 238.679 78.7925 235.96 76.1787C233.241 73.565 231.881 69.3709 231.881 63.5964V18.9888L244.118 18.9888V62.0464C244.118 64.3562 244.553 65.9062 245.423 66.6964C246.348 67.4258 247.87 67.7905 249.992 67.7905H257.334V80.0993ZM257.334 45.7258H225.436V34.4199H257.334V45.7258Z"
      fill="currentColor"
    />
    <path
      d="M191.769 81.0111C187.962 81.0111 184.78 80.2209 182.224 78.6405C179.722 76.9994 177.846 74.8111 176.595 72.0758C175.399 69.3405 174.801 66.2709 174.801 62.867V34.4199H187.037V58.3082C187.037 61.9552 187.826 64.6297 189.403 66.3317C190.98 67.9729 193.7 68.7935 197.561 68.7935C201.586 68.7935 204.387 67.9121 205.964 66.1493C207.595 64.3258 208.411 61.469 208.411 57.5788L209.961 57.4876L210.858 66.1493H208.574C208.248 68.7023 207.432 71.1033 206.127 73.3523C204.876 75.6013 203.054 77.4552 200.661 78.9141C198.322 80.3121 195.358 81.0111 191.769 81.0111ZM220.648 80.0994H209.227V65.967L208.411 65.5111V34.4199H220.648V80.0994Z"
      fill="currentColor"
    />
    <path
      d="M148.55 80.0995H136.313V19.0112H148.55V80.0995ZM168.863 31.3201H116V19.0112H168.863V31.3201Z"
      fill="currentColor"
    />
    <defs>
      <linearGradient
        id="tt-foot-logo-p0"
        x1="3.93732"
        y1="47.0477"
        x2="79.3971"
        y2="90.4666"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0000FF" />
        <stop offset="0.75" stopColor="#2AB8F0" />
      </linearGradient>
      <linearGradient
        id="tt-foot-logo-p1"
        x1="59.5846"
        y1="87.8522"
        x2="85.705"
        y2="12.7719"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0000FF" />
        <stop offset="0.75" stopColor="#2EF8C8" />
      </linearGradient>
      <linearGradient
        id="tt-foot-logo-p2"
        x1="78.3084"
        y1="21.3789"
        x2="0.636641"
        y2="38.0146"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0000FF" />
        <stop offset="0.75" stopColor="#FF75C5" />
      </linearGradient>
      <clipPath id="tt-foot-logo-clip0">
        <rect width="100" height="100" fill="#fff" />
      </clipPath>
      <clipPath id="tt-foot-logo-clip1">
        <rect width="100" height="100" fill="#fff" />
      </clipPath>
    </defs>
  </svg>
)

export const Footer = () => {
  const { t } = useTranslation("footer")
  const year = new Date().getFullYear()

  return (
    <div className="tt-foot-root">
      <footer className="tt-foot">
        <div className="tt-foot-wrap">
          <div className="tt-foot-top">
            <div className="tt-foot-brand">
              <a
                href={`${SITE}/?${UTM}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tt-foot-logo"
                aria-label={t("home")}
              >
                <TutellusLogo />
              </a>
              <p className="tt-foot-tagline">
                {t("tagline")}
                <br />
                <a className="tt-foot-mail" href="mailto:hola@tutellus.com">
                  hola@tutellus.com
                </a>
              </p>
              <div className="tt-foot-social">
                {SOCIAL.map(({ label, href, icon, brand }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                    <svg
                      className={brand ? "tt-foot-ico tt-foot-ico--brand" : "tt-foot-ico"}
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      {icon}
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <div className="tt-foot-cols">
              {COLUMNS.map(({ id, titleKey, links }) => (
                <div key={id} className={`tt-foot-col is-${id}`}>
                  <h2>{t(titleKey)}</h2>
                  {links.map(({ key, href }) => (
                    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
                      {t(key)}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="tt-foot-bottom">
            <div className="tt-foot-copy">{t("copy", { year })}</div>
            <div className="tt-foot-legal">
              {LEGAL.map(({ key, href }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer">
                  {t(key)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
